// src/services/villa.js
import { apiClient } from './api';

export const villaService = {
  // Get all properties with filters
  getProperties: async (params = {}) => {
    try {
      const response = await apiClient.get('/properties', { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching properties:', error);
      throw error;
    }
  },

  // Get featured properties
  getFeaturedProperties: async () => {
    try {
      const response = await apiClient.get('/properties/featured');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching featured properties:', error);
      throw error;
    }
  },

  // Get properties by category
  getPropertiesByCategory: async (categoryId) => {
    try {
      const response = await apiClient.get('/properties', {
        params: { category_id: categoryId, status: 'active' }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching properties by category:', error);
      throw error;
    }
  },

  // Get properties by location
  getPropertiesByLocation: async (locationId) => {
    try {
      const response = await apiClient.get('/properties', {
        params: { location_id: locationId, status: 'active' }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching properties by location:', error);
      throw error;
    }
  },

  // Search properties
  searchProperties: async (query) => {
    try {
      const response = await apiClient.get('/properties', {
        params: { search: query, status: 'active' }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error searching properties:', error);
      throw error;
    }
  },

  // Get property by ID
  getPropertyById: async (id) => {
    try {
      const response = await apiClient.get(`/properties/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching property by ID:', error);
      throw error;
    }
  },

  // Create new property
  create: async (formData) => {
    try {
      const response = await apiClient.post('/properties', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating property:', error);
      throw error;
    }
  },

  // Update property
  update: async (id, formData) => {
    try {
      const response = await apiClient.put(`/properties/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating property:', error);
      throw error;
    }
  },

  // Delete property
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/properties/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting property:', error);
      throw error;
    }
  },

  // Update property status
  updateStatus: async (id, status) => {
    try {
      const response = await apiClient.patch(`/properties/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating property status:', error);
      throw error;
    }
  },

  // Toggle featured status
  toggleFeatured: async (id) => {
    try {
      const response = await apiClient.patch(`/properties/${id}/featured`);
      return response.data;
    } catch (error) {
      console.error('Error toggling featured status:', error);
      throw error;
    }
  },

  // ADDED: Individual dropdown functions to villaService for backward compatibility
  getCategories: async () => {
    try {
      const response = await apiClient.get('/dropdown/categories');
      return response?.data?.data || [];
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  getLocations: async () => {
    try {
      const response = await apiClient.get('/dropdown/locations');
      return response?.data?.data || [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  },

  getFacilities: async () => {
    try {
      const response = await apiClient.get('/dropdown/facilities');
      return response?.data?.data || [];
    } catch (error) {
      console.error('Error fetching facilities:', error);
      throw error;
    }
  }
};

// Dropdown Service - API untuk mendapatkan data dropdown
export const dropdownService = {
  // Get all dropdown data in one request (recommended)
  getAllDropdownData: async () => {
    try {
      console.log('Fetching all dropdown data from API...');
      const response = await apiClient.get('/dropdown/all');
      return response?.data?.data || { categories: [], locations: [], facilities: [] };
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      // If /dropdown/all endpoint doesn't exist, fallback to individual calls
      if (error.response?.status === 404) {
        console.log('Fallback: using individual dropdown endpoints...');
        try {
          const [categories, locations, facilities] = await Promise.all([
            dropdownService.getCategories(),
            dropdownService.getLocations(),
            dropdownService.getFacilities()
          ]);
          return { categories, locations, facilities };
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError);
          throw fallbackError;
        }
      }
      throw error;
    }
  },

  // Get categories only
  getCategories: async () => {
    try {
      const response = await apiClient.get('/dropdown/categories');
      return response?.data?.data || [];
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Get locations only
  getLocations: async () => {
    try {
      const response = await apiClient.get('/dropdown/locations');
      return response?.data?.data || [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  },

  // Get facilities only
  getFacilities: async () => {
    try {
      const response = await apiClient.get('/dropdown/facilities');
      return response?.data?.data || [];
    } catch (error) {
      console.error('Error fetching facilities:', error);
      throw error;
    }
  },

  // Add new category (admin only)
  addCategory: async (categoryData) => {
    try {
      const response = await apiClient.post('/dropdown/categories', categoryData);
      return response.data;
    } catch (error) {
      console.error('Error adding category:', error);
      throw error;
    }
  },

  // Add new location (admin only)
  addLocation: async (locationData) => {
    try {
      const response = await apiClient.post('/dropdown/locations', locationData);
      return response.data;
    } catch (error) {
      console.error('Error adding location:', error);
      throw error;
    }
  },

  // Add new facility (admin only)
  addFacility: async (facilityData) => {
    try {
      const response = await apiClient.post('/dropdown/facilities', facilityData);
      return response.data;
    } catch (error) {
      console.error('Error adding facility:', error);
      throw error;
    }
  },

  // Delete category (admin only)
  deleteCategory: async (id) => {
    try {
      const response = await apiClient.delete(`/dropdown/categories/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  },

  // Delete location (admin only)
  deleteLocation: async (id) => {
    try {
      const response = await apiClient.delete(`/dropdown/locations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting location:', error);
      throw error;
    }
  },

  // Delete facility (admin only)
  deleteFacility: async (id) => {
    try {
      const response = await apiClient.delete(`/dropdown/facilities/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting facility:', error);
      throw error;
    }
  }
};

// Named exports untuk backward compatibility
export const getProperties = villaService.getProperties;
export const getFeaturedProperties = villaService.getFeaturedProperties;
export const getPropertiesByCategory = villaService.getPropertiesByCategory;
export const getPropertiesByLocation = villaService.getPropertiesByLocation;
export const searchProperties = villaService.searchProperties;
export const getPropertyById = villaService.getPropertyById;
export const createProperty = villaService.create;
export const updateProperty = villaService.update;
export const deleteProperty = villaService.delete;
export const updatePropertyStatus = villaService.updateStatus;
export const toggleFeaturedStatus = villaService.toggleFeatured;

// Export dropdown functions
export const getAllDropdownData = dropdownService.getAllDropdownData;
export const getCategories = dropdownService.getCategories;
export const getLocations = dropdownService.getLocations;
export const getFacilities = dropdownService.getFacilities;